/**
 * =============================================================================
 * ENTRY POINT - Inicialização da Aplicação
 * =============================================================================
 * Ordem de importância:
 * 1. React Core
 * 2. CSS Global
 * 3. Configurações Puras (supabase)
 * 4. Contexts (Theme -> Auth -> Project)
 * 5. Components
 */

import React, { useEffect, type ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// CSS Global PRIMEIRO
import './src/index.css';

// Contexts - importar SEM barrel exports para evitar circular deps
import { AuthProvider } from './src/context/AuthContext';
import { ThemeProvider } from './src/context/ThemeContext';
import { ProjectProvider } from './src/context/ProjectContext';
import { useAuth } from './src/context/AuthContext';
import { useWorkspace, WorkspaceProvider } from './src/contexts/WorkspaceContext';
import { OperationalContextProvider } from './src/contexts/OperationalContext';
import { ExecutionSelectionProvider } from './src/contexts/ExecutionSelectionContext';
import { LicenseProvider } from './src/context/LicenseContext';
import LicenseActivationPage from './src/pages/LicenseActivationPage';

// Components
import AppRouter from './src/components/AppRouter'; 
import PWAUpdatePrompt from './src/components/PWAUpdatePrompt';
import { DrawerHostProvider } from './src/components/drawer/DrawerHostProvider';
import { HelpProvider } from './src/components/HelpSystem';
import ErrorBoundary from './src/components/ErrorBoundary';

// Error monitoring — initialize early
import { initErrorMonitoring, setErrorContext } from './src/services/errorMonitoringService';
initErrorMonitoring();

// React Query - instância única para toda a aplicação
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

const ErrorContextSync: React.FC = () => {
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  useEffect(() => {
    setErrorContext({
      userId: user?.id,
      workspaceId: workspace?.organizationId,
      organizationId: workspace?.organizationId,
    });
  }, [user?.id, workspace?.organizationId]);
  return null;
};

const WorkspaceBridge: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, memberships, setActiveOrganizationMembership } = useAuth();
  return (
    <WorkspaceProvider
      userId={user?.id}
      memberships={memberships}
      onActiveOrganizationChange={setActiveOrganizationMembership}
    >
      <ErrorContextSync />
      {children}
    </WorkspaceProvider>
  );
};

/**
 * LicenseGate — wraps the app with LicenseProvider but only on Electron.
 * On the web, licensing is handled via Supabase subscriptions, not serial keys.
 */
const PUBLIC_HASHES = ['#/', '#/welcome', '#/login', '#/termos', '#/privacidade', '#/setup', '#/activate'];

const LicenseGate: React.FC<{ children: ReactNode }> = ({ children }) => {
  const isElectron = !!(window as any).electronAPI?.isElectron;

  // Web mode: skip license gate entirely — auth is via Supabase
  if (!isElectron) {
    return <>{children}</>;
  }

  const hash = window.location.hash;
  const isPublicRoute = PUBLIC_HASHES.some(p => hash.startsWith(p));

  if (isPublicRoute) {
    return <>{children}</>;
  }

  return (
    <LicenseProvider fallback={<LicenseActivationPage />}>
      {children}
    </LicenseProvider>
  );
};

// =============================================================================
// INICIALIZAÇÃO
// =============================================================================

// Garantir que o elemento root existe
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error("Elemento 'root' não encontrado no HTML");
}

// Reuse existing root on HMR to avoid "createRoot on already-used container" error
const _g = globalThis as any;
if (!_g.__WORKFLOW_ROOT) {
  _g.__WORKFLOW_ROOT = ReactDOM.createRoot(rootElement);
}
const root = _g.__WORKFLOW_ROOT;

// Estrutura da aplicação com ordem correta de Providers
const App = (
  <ErrorBoundary>
    <HashRouter>
      <QueryClientProvider client={queryClient}>
        <LicenseGate>
          <AuthProvider>
            <WorkspaceBridge>
            <ThemeProvider>
              <ProjectProvider>
                <OperationalContextProvider>
                <ExecutionSelectionProvider>
                  <DrawerHostProvider>
                    <HelpProvider>
                      <AppRouter />
                      <PWAUpdatePrompt />
                    </HelpProvider>
                  </DrawerHostProvider>
                </ExecutionSelectionProvider>
                </OperationalContextProvider>
              </ProjectProvider>
            </ThemeProvider>
          </WorkspaceBridge>
        </AuthProvider>
        </LicenseGate>
      </QueryClientProvider>
    </HashRouter>
  </ErrorBoundary>
);

// Renderizar sem StrictMode em produção para evitar double-init
const isProd = import.meta.env.PROD;

if (isProd) {
  // Produção: sem StrictMode
  root.render(App);
} else {
  // Desenvolvimento: com StrictMode para detectar problemas
  root.render(<React.StrictMode>{App}</React.StrictMode>);
}

// Log de inicialização
console.log('✅ App inicializado', { isProd, mode: import.meta.env.MODE });
